import { Popover, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { IGlobalState } from '~reducer';
import Layout from './Layout';
import { currencyFormatter } from '~utilities/formatters';
import { Dispatch } from 'redux';
import { IDictionary } from '~utilities/IDictionary';
import IGroupedWarehouseLoanTransaction from '~LoanPaymentAbas/IGroupedWarehouseLoanTransaction';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import { warehousesSelector } from '~Warehouses/selectors';
import IWarehouse from '~Api/Warehouse/IWarehouse';
import { warehousesListAction } from '~Warehouses/actions';
import { loanPaymentAbaWarehouseLoanTransactionsSelector } from '~LoanPaymentAbas/selectors';
import { loanPaymentAbaWarehouseLoanTransactionsListAction } from '~LoanPaymentAbas/actions';

export default function Transactions(): ReactElement {
    const { loanPaymentAbaUuid } = useParams<{ loanPaymentAbaUuid: string }>();

    const transactions: IDictionary<IWarehouseLoanTransaction> = useSelector((state: IGlobalState) => loanPaymentAbaWarehouseLoanTransactionsSelector(state, loanPaymentAbaUuid));
    const warehouses: IDictionary<IWarehouse> = useSelector(warehousesSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!transactions) {
            dispatch(loanPaymentAbaWarehouseLoanTransactionsListAction(loanPaymentAbaUuid));
        }
    }, [
        dispatch,
        loanPaymentAbaUuid,
        transactions,
    ]);

    useEffect(() => {
        if (!warehouses) {
            dispatch(warehousesListAction());
        }
    }, [
        dispatch,
        warehouses,
    ]);

    const summary: (data: readonly IGroupedWarehouseLoanTransaction[]) => JSX.Element = useCallback((data: readonly IGroupedWarehouseLoanTransaction[]): JSX.Element => {
        const totalInterest: number = _.reduce(data, (totalInterest: number, transaction: IGroupedWarehouseLoanTransaction) => totalInterest + transaction.totalInterest, 0);

        return (
            <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                <Table.Summary.Cell index={1}>{data.length}</Table.Summary.Cell>
                <Table.Summary.Cell index={2}>{currencyFormatter.format(totalInterest)}</Table.Summary.Cell>
            </Table.Summary.Row>
        );
    }, []);

    if (!transactions || !warehouses) {
        return (
            <Layout pageTitle='Transactions' section='transactions' uuid={loanPaymentAbaUuid}>
                <Typography.Title level={2}>Transactions</Typography.Title>
                <Spin/>
            </Layout>
        );
    }

    const columns: ColumnsType<IGroupedWarehouseLoanTransaction> = [
        {
            dataIndex: 'warehouseLoanTransactionTime',
            defaultSortOrder: 'descend',
            render: (warehouseLoanTransactionTime: string) => dayjs(warehouseLoanTransactionTime).format('DD/MM/YYYY HH:mm:ss'),
            sorter: (a: IGroupedWarehouseLoanTransaction, b: IGroupedWarehouseLoanTransaction) => dayjs(a.warehouseLoanTransactionTime) > dayjs(b.warehouseLoanTransactionTime) ? 1 : -1,
            title: 'Transaction Time',
            width: 200,
        },
        {
            dataIndex: 'loanCode',
            render: (loanCode: string, row: IGroupedWarehouseLoanTransaction): ReactNode => {
                const content: ReactNode = _.map(row.transactionUuids, (transactionUuid: string) => (
                    <p key={transactionUuid}>
                        {warehouses[transactions[transactionUuid].warehouseUuid].type}: {currencyFormatter.format(transactions[transactionUuid].amountPrepaidInterest * -1)}
                    </p>
                ));

                return (
                    <Space>
                        <Link to={`/loans/${row.loanUuid}`}>{loanCode}</Link>
                        {row.transactionUuids.length > 1 && <Popover content={content}><Tag>Split</Tag></Popover>}
                    </Space>
                );
            },
            title: 'Loan',
        },
        {
            dataIndex: 'totalInterest',
            render: (totalInterest: number) => currencyFormatter.format(totalInterest),
            title: 'Interest',
            width: 200,
        },
    ];

    const groupedTransactions: IDictionary<IGroupedWarehouseLoanTransaction> = {};
    _.each(transactions, (transaction: IWarehouseLoanTransaction) => {
        const key: string = transaction.loanTransactionUuid;

        if (!groupedTransactions[key]) {
            groupedTransactions[key] = {
                loanCode: transaction.loanCode,
                loanTransactionUuid: transaction.loanTransactionUuid,
                loanUuid: transaction.loanUuid,
                totalInterest: 0,
                transactionUuids: [],
                warehouseLoanTransactionTime: transaction.transactionTime,
            };
        }

        groupedTransactions[key].totalInterest += transaction.amountPrepaidInterest * -1;
        groupedTransactions[key].transactionUuids.push(transaction.uuid);
    });

    return (
        <Layout pageTitle='Transactions' section='transactions' uuid={loanPaymentAbaUuid}>
            <Typography.Title level={2}>Transactions</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(groupedTransactions)}
                pagination={false}
                rowKey='loanTransactionUuid'
                size='middle'
                summary={summary}
            />
        </Layout>
    );
}